import type { LngLat } from "../types";
import type { GroundPoint } from "./dugArea";
import { clearingsOf, indexRoads, roadsNear } from "./roadClearings";
import type { IndexedRoad, RoadFeature } from "./roadClearings";
import { computeScatterArea } from "./surfaceScatterSampling";
import type { ScatterArea, ScatterContribution } from "./surfaceScatterSampling";

/** Le repère de la scène du décor : son origine en coordonnées Mercator, et la taille d'un mètre. */
export interface ScatterFrame {
  x: number;
  y: number;
  meterScale: number;
}

export interface ScatterAreaRequest {
  key: string;
  area: ScatterArea;
}

export type ScatterRequest =
  | { id: number; type: "compute"; areas: ScatterAreaRequest[]; openings: GroundPoint[][] }
  /** Les routes ont changé : `areas` sont celles affichées, seules celles dont le dégagement change sont refaites. */
  | { id: number; type: "roads"; roads: RoadFeature[] | null; frame: ScatterFrame; areas: ScatterAreaRequest[]; openings: GroundPoint[][] };

export interface ScatterResult {
  key: string;
  contributions: ScatterContribution[];
}

export interface ScatterResponse {
  id: number;
  results: ScatterResult[];
}

function toLocal([lng, lat]: LngLat, frame: ScatterFrame): GroundPoint {
  const x = (180 + lng) / 360;
  const y = (180 - (180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))) / 360;
  return [(x - frame.x) / frame.meterScale, (y - frame.y) / frame.meterScale];
}

/**
 * Le semis et le dégagement des routes, hors du fil principal : les routes arrivent tuile par tuile,
 * et y recalculer à chaque fois ce qui passe sur chaque forme figeait la carte.
 */
export class ScatterComputer {
  private roads: IndexedRoad[] | null = null;
  private frame: ScatterFrame | null = null;
  /** Les routes retenues pour chaque zone à son dernier semis, pour ne refaire que celles qui changent. */
  private readonly clearingKeys = new Map<string, string>();

  handle(request: ScatterRequest): ScatterResult[] {
    if (request.type === "compute") return request.areas.map(({ key, area }) => this.compute(key, area, request.openings));
    this.roads = request.roads ? indexRoads(request.roads) : null;
    this.frame = request.frame;
    const shown = new Set(request.areas.map(({ key }) => key));
    for (const key of [...this.clearingKeys.keys()]) if (!shown.has(key)) this.clearingKeys.delete(key);
    return request.areas.flatMap(({ key, area }) => {
      if (!area.roadBounds) return [];
      const near = this.near(area);
      if (this.keyOf(near) === (this.clearingKeys.get(key) ?? "")) return [];
      return [this.compute(key, area, request.openings, near)];
    });
  }

  private near(area: ScatterArea): IndexedRoad[] {
    return this.roads && area.roadBounds ? roadsNear(this.roads, area.roadBounds) : [];
  }

  private keyOf(roads: IndexedRoad[]): string {
    return roads.map((road) => road.key).sort().join("\n");
  }

  private compute(key: string, area: ScatterArea, openings: GroundPoint[][], near = this.near(area)): ScatterResult {
    this.clearingKeys.set(key, this.keyOf(near));
    const frame = this.frame;
    if (!near.length || !frame) return { key, contributions: computeScatterArea(area, openings) };
    const { lines, areas } = clearingsOf(near);
    const clearings = {
      lines: lines.map(({ path, half }) => ({ path: path.map((point) => toLocal(point, frame)), half })),
      areas: areas.map((ring) => ring.map((point) => toLocal(point, frame))),
    };
    return { key, contributions: computeScatterArea({ ...area, clearings }, openings) };
  }
}
